import { ApiService } from "../services/apiService";
import { LaravelPaginatedResponse } from "../types/user";
import { PaginatedResult } from "../types/api";
import { logger } from "./logger";

// Unwraps the API's double-wrapped list envelope: { data: { data: [...] } }.
// Returns null on an unexpected shape so callers can surface a failure.
export function unwrapPaginated<T>(
  result: { data?: LaravelPaginatedResponse<T> } | null | undefined,
  context: string
): PaginatedResult<T> | null {
  const paginator = result?.data;

  if (!paginator || !Array.isArray(paginator.data)) {
    logger.error(`Unexpected ${context} API response format:`, result);
    return null;
  }

  return {
    items: paginator.data,
    total: paginator.total,
    page: paginator.current_page,
    limit: paginator.per_page,
    lastPage: paginator.last_page,
  };
}

/**
 * Helper function to fetch all pages of paginated data from an API endpoint
 * @param endpoint - The API endpoint to fetch from
 * @param dataKey - The key in the response data where the array is located (default: 'data')
 * @returns Promise<T[]> - All data combined from all pages
 */
export async function fetchAllPages<T>(
  endpoint: string,
  dataKey: string = "data"
): Promise<T[]> {
  const allData: T[] = [];
  let currentPage = 1;
  let hasNextPage = true;

  // A failed page used to be caught here and the partial result returned as
  // if it were complete, so a dropped request looked like an empty list.
  // Let it throw; callers already return [] or null on error.
  while (hasNextPage) {
    const separator = endpoint.includes("?") ? "&" : "?";
    const result = await ApiService.getJson<any>(
      `${endpoint}${separator}page=${currentPage}`
    );

    const rows = result?.data?.[dataKey];
    if (!Array.isArray(rows)) break;

    allData.push(...rows);

    // last_page is the authoritative signal. The previous OR-chain also
    // accepted next_page_url and a per_page*page arithmetic check, either of
    // which could stay true past the end and loop again.
    const { current_page, last_page } = result.data;
    hasNextPage =
      typeof current_page === "number" &&
      typeof last_page === "number" &&
      current_page < last_page;

    currentPage++;
  }

  return allData;
}
